import { useMutation, useQuery } from "@tanstack/react-query";

import { can } from "@devengo/shared";

import { Button, Card, ErrorBox, Notice } from "../components/ui";
import { api } from "../lib/api";
import { ROLE_LABELS, useBranding, useCurrentUser } from "../lib/session";

const PARAMS = [
  "response_type",
  "client_id",
  "redirect_uri",
  "code_challenge",
  "code_challenge_method",
  "state",
  "resource",
  "scope",
] as const;

function authorizeParams(): Record<string, string> {
  const query = new URLSearchParams(window.location.search);
  return Object.fromEntries(
    PARAMS.flatMap((k) => (query.get(k) ? [[k, query.get(k) as string]] : [])),
  );
}

/**
 * OAuth authorization endpoint (spec 013, contracts/oauth.md): a signed-in studio user lets an AI
 * assistant act as them. The page is the studio's (white-label); the assistant gets exactly this
 * user's role and loses access when the user revokes it, is deactivated or changes password.
 */
export function ConnectPage() {
  const branding = useBranding();
  const user = useCurrentUser();
  const params = authorizeParams();
  const request = useQuery({
    queryKey: ["oauth-request", params],
    queryFn: () =>
      api.get<{ clientName: string }>(
        `/api/oauth/authorize/request?${new URLSearchParams(params).toString()}`,
      ),
    retry: false,
  });
  const decide = useMutation({
    mutationFn: (decision: "approve" | "deny") =>
      api.post<{ redirectTo: string }>("/api/oauth/authorize", { ...params, decision }),
    onSuccess: ({ redirectTo }) => window.location.assign(redirectTo),
  });

  return (
    <div className="flex min-h-[70vh] items-center justify-center">
      <Card className="w-full max-w-lg" title="Conectar un asistente">
        {request.isLoading ? (
          <p className="text-sm text-brand-muted">Verificando el pedido…</p>
        ) : null}
        <ErrorBox error={request.error} />
        {request.data && !can(user.role, "connectAssistant") ? (
          <div className="flex flex-col gap-4 text-sm">
            <p>
              <strong>{request.data.clientName}</strong> quiere acceder a{" "}
              <strong>{branding.displayName}</strong> como <strong>{user.name}</strong>.
            </p>
            <Notice tone="warning">
              Tu rol ({ROLE_LABELS[user.role]}) no puede conectar asistentes de IA. Pedile al Dueño
              del estudio que lo conecte.
            </Notice>
            <ErrorBox error={decide.error} />
            <div className="flex justify-end">
              <Button
                variant="secondary"
                disabled={decide.isPending}
                onClick={() => decide.mutate("deny")}
              >
                Volver al asistente
              </Button>
            </div>
          </div>
        ) : request.data ? (
          <div className="flex flex-col gap-4 text-sm">
            <p>
              <strong>{request.data.clientName}</strong> quiere acceder a{" "}
              <strong>{branding.displayName}</strong> como <strong>{user.name}</strong> (
              {ROLE_LABELS[user.role]}).
            </p>
            <ul className="list-disc pl-5 text-brand-muted">
              <li>
                Podrá ver y hacer todo lo que tu rol permite: cargar y editar comprobantes, importar
                archivos, la Caja IVA, los períodos
                {user.role === "dueno" ? ", la marca y los usuarios" : ""}.
              </li>
              <li>Nunca calcula importes: la Caja IVA y los libros los calcula el sistema.</li>
              <li>
                Anular o mover comprobantes, cerrar, reabrir o presentar períodos, confirmar
                importaciones y cambiar roles, desactivar usuarios o resetear claves le van a pedir
                tu confirmación en el chat.
              </li>
              <li>Todo lo que haga queda en la auditoría como hecho por vos desde el asistente.</li>
            </ul>
            <Notice>Podés desconectarlo cuando quieras desde «Asistentes».</Notice>
            <ErrorBox error={decide.error} />
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                disabled={decide.isPending}
                onClick={() => decide.mutate("deny")}
              >
                Rechazar
              </Button>
              <Button disabled={decide.isPending} onClick={() => decide.mutate("approve")}>
                Permitir
              </Button>
            </div>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
